import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type PayGatePaymentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/PayGatePaymentModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import PayGatePaymentModuleActions from './PayGatePaymentModuleActions';
import PayGatePaymentSettingsEmptyList from './PayGatePaymentSettingsEmptyList';
import PayGatePaymentSettingsTable from './PayGatePaymentSettingsTable';

export type PayGatePaymentModulePageProps = { module: PayGatePaymentModuleWithPermissionsSpecsFragment };

const PayGatePaymentModulePage = ({ module }: PayGatePaymentModulePageProps) => {
    const { t } = useTranslation(['paygatePaymentModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('paygatePaymentModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('paygatePaymentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<PayGatePaymentModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('paygatePaymentModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <Divider>{t('paygatePaymentModuleDetails:dividers.mainDetails')}</Divider>
            {module.paymentSettings.length === 0 ? (
                <PayGatePaymentSettingsEmptyList module={module} />
            ) : (
                <PayGatePaymentSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default PayGatePaymentModulePage;
